import {
  DynamicObjectDefinition,
  DynamicObjectResult,
  DynamicObjectValues,
  type DynamicProperty,
} from '../../../../../../apiRPC/dynamicObject';
import { RCardGet } from '../../../../../../apiRPC/card';
import { AGENCY_NAME_OPTIONS } from './agencyNameOptions';
import { CLIENT_NAME_OPTIONS } from './clientNameOptions';

// Типы перечислений для селектов
export type AdditionalFormat = 'BANNERS' | 'VIDEO' | 'NATIVE';

export type AdditionalBilling = 'CPM' | 'CPC' | 'CPA';

export type AdditionalProduct = 'Медиа' | 'ССП' | 'Иннолаба';

export type AdditionalTrafficType = 'In-App' | 'Web' | 'Smart TV';

export type AdditionalCategory =
  | 'Фулл-сервис'
  | 'Контекст'
  | 'Селф-сервис'
  | 'ССП'
  | 'Дин. рет ТТ'
  | 'Прочее';

export type AdditionalOrd = 'ozon' | 'beeline' | 'VK';

export type AdditionalMaterialType =
  | 'внешний код'
  | 'материалы на наших серверах';

export type AdditionalFieldsModel = {
  agencyName?: string;
  clientName?: string;
  campaignName?: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  format?: AdditionalFormat | null;
  billing?: AdditionalBilling | null;
  trafficType?: AdditionalTrafficType | null;
  volume?: number | null;
  clientCpm?: number | null;
  clientBudget?: number | null;
  /** Клиентский бюджет на текущий месяц (ручной ввод) */
  clientBudgetCurrentMonth?: number | null;
  /** Есть разбивка по месяцам */
  hasMonthlyBreakdown?: boolean | null;
  /** Клиентский бюджет за предыдущие периоды (ручной ввод) */
  clientBudgetPreviousPeriods?: number | null;
  /** Клиентский бюджет на текущий месяц из брони (ручной ввод, default = 0) */
  clientBudgetCurrentMonthFromBooking?: number | null;
  /** ID grid архив — ручной текстовый ввод без формул */
  gridArchiveId?: string | null;
  returnDiscountPercent?: number | null;
  /** Сумма возвратной СК */
  returnDiscountSum?: number | null;
  payCpm?: number | null;
  /** Поле в модели/динамическом объекте по-прежнему называется payBudget,
   *  но по бизнес-смыслу/лейблу в UI это \"Выручка\" */
  payBudget?: number | null;
  adFeePercent?: number | null;
  adFeeSum?: number | null;
  /** Новый \"Бюджет к оплате нам\" после вычета возвратной СК и рекламного сбора */
  budgetToPay?: number | null;
  statsUrl?: string | null;
  product?: AdditionalProduct | null;
  category?: AdditionalCategory | null;
  ord?: AdditionalOrd | null;
  verifiers?: string | null;
  materialType?: AdditionalMaterialType | null;
  hasContainer?: boolean | null;
  hasFunnelByContainer?: boolean | null;
  hasQualityAudiencePurchase?: boolean | null;
  /** Участники АМ — множественный выбор */
  amParticipants?: string[] | null;
  /** Участники ТМ — множественный выбор */
  tmParticipants?: string[] | null;
  /** Участники СМ — множественный выбор */
  cmParticipants?: string[] | null;
};

export type AdditionalFieldKey = keyof AdditionalFieldsModel;

export type AdditionalBooleanFieldKey = Extract<
  AdditionalFieldKey,
  | 'hasContainer'
  | 'hasMonthlyBreakdown'
  | 'hasFunnelByContainer'
  | 'hasQualityAudiencePurchase'
>;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const roundTo4 = (value: number | null | undefined): number | null => {
  if (!isFiniteNumber(value ?? null)) return null;
  return Math.round((value as number) * 10000) / 10000;
};

export function calculateClientBudget(
  billing: AdditionalBilling | null | undefined,
  volume: number | null | undefined,
  clientCpm: number | null | undefined,
): number | null {
  if (!isFiniteNumber(volume) || !isFiniteNumber(clientCpm)) return null;

  if (billing === 'CPM') {
    return roundTo4((volume * clientCpm) / 1000);
  }

  if (billing === 'CPA' || billing === 'CPC') {
    return roundTo4(volume * clientCpm);
  }

  return null;
}

export function calculateReturnDiscountSum(
  clientBudget: number | null | undefined,
  returnDiscountPercent: number | null | undefined,
): number | null {
  if (!isFiniteNumber(clientBudget) || !isFiniteNumber(returnDiscountPercent)) {
    return null;
  }
  return roundTo4((clientBudget * returnDiscountPercent) / 100);
}

export function calculateAdFeeSum(
  clientBudget: number | null | undefined,
  adFeePercent: number | null | undefined,
): number | null {
  if (!isFiniteNumber(clientBudget) || !isFiniteNumber(adFeePercent)) {
    return null;
  }
  return roundTo4((clientBudget * adFeePercent) / 100);
}

export function calculateRevenue(
  clientBudget: number | null | undefined,
  returnDiscountSum: number | null | undefined,
): number | null {
  if (!isFiniteNumber(clientBudget) || !isFiniteNumber(returnDiscountSum)) {
    return null;
  }
  return roundTo4(clientBudget - returnDiscountSum);
}

export function calculateBudgetToPay(
  clientBudget: number | null | undefined,
  returnDiscountSum: number | null | undefined,
  adFeeSum: number | null | undefined,
): number | null {
  if (
    !isFiniteNumber(clientBudget) ||
    !isFiniteNumber(returnDiscountSum) ||
    !isFiniteNumber(adFeeSum)
  ) {
    return null;
  }
  return roundTo4(clientBudget - returnDiscountSum - adFeeSum);
}

export function recalculateFinancialFields(
  values: AdditionalFieldsModel,
): AdditionalFieldsModel {
  const next: AdditionalFieldsModel = { ...values };

  const clientBudget = calculateClientBudget(
    values.billing ?? null,
    values.volume ?? null,
    values.clientCpm ?? null,
  );

  next.clientBudget = clientBudget;

  const returnDiscountSum = calculateReturnDiscountSum(
    next.clientBudget ?? null,
    values.returnDiscountPercent ?? null,
  );

  next.returnDiscountSum = returnDiscountSum;

  const adFeeSum = calculateAdFeeSum(
    next.clientBudget ?? null,
    values.adFeePercent ?? null,
  );

  next.adFeeSum = adFeeSum;

  const revenue = calculateRevenue(
    next.clientBudget ?? null,
    next.returnDiscountSum ?? null,
  );

  next.payBudget = revenue;

  const budgetToPay = calculateBudgetToPay(
    next.clientBudget ?? null,
    next.returnDiscountSum ?? null,
    next.adFeeSum ?? null,
  );

  next.budgetToPay = budgetToPay;

  return next;
}

type AdditionalFieldMeta = {
  label: string;
  type:
    | 'string'
    | 'number'
    | 'boolean'
    | 'date'
    | 'select'
    | 'url'
    | 'autocomplete'
    | 'multiselect';
  enumValues?: string[];
  options?: string[];
  required?: boolean;
};

export const ADDITIONAL_FIELDS_META: Record<
  AdditionalFieldKey,
  AdditionalFieldMeta
> = {
  agencyName: {
    label: 'Название Рекламного Агентства',
    type: 'autocomplete',
    options: AGENCY_NAME_OPTIONS,
  },
  clientName: {
    label: 'Название Партнера/Клиента',
    type: 'autocomplete',
    options: CLIENT_NAME_OPTIONS,
  },
  campaignName: {
    label: 'Название Кампании',
    type: 'string',
  },
  periodStart: {
    label: 'Период — начало',
    type: 'date',
  },
  periodEnd: {
    label: 'Период — конец',
    type: 'date',
  },
  format: {
    label: 'Формат',
    type: 'select',
    enumValues: ['', 'BANNERS', 'VIDEO', 'NATIVE'],
  },
  billing: {
    label: 'Биллинг',
    type: 'select',
    enumValues: ['', 'CPA', 'CPC', 'CPM'],
  },
  trafficType: {
    label: 'Тип трафика',
    type: 'select',
    enumValues: ['', 'In-App', 'Web', 'Smart TV'],
  },
  volume: {
    label: 'Объем',
    type: 'number',
  },
  clientCpm: {
    label: 'Цена',
    type: 'number',
  },
  clientBudget: {
    label: 'Бюджет клиентский',
    type: 'number',
  },
  clientBudgetCurrentMonth: {
    label: 'Клиентский бюджет на текущий месяц',
    type: 'number',
  },
  hasMonthlyBreakdown: {
    label: 'Есть разбивка по месяцам',
    type: 'boolean',
  },
  clientBudgetPreviousPeriods: {
    label: 'Клиентский бюджет за предыдущие периоды',
    type: 'number',
  },
  clientBudgetCurrentMonthFromBooking: {
    label: 'Клиентский бюджет на текущий месяц из брони',
    type: 'number',
  },
  gridArchiveId: {
    label: 'ID grid архив',
    type: 'string',
  },
  returnDiscountPercent: {
    label: 'Возвратная СК, %',
    type: 'number',
  },
  returnDiscountSum: {
    label: 'Сумма возвратной СК',
    type: 'number',
  },
  payCpm: {
    label: 'Цена к оплате нам, ₽',
    type: 'number',
  },
  payBudget: {
    label: 'Выручка',
    type: 'number',
  },
  adFeePercent: {
    label: 'Рекламный сбор, %',
    type: 'number',
  },
  adFeeSum: {
    label: 'Сумма рекламного сбора',
    type: 'number',
  },
  budgetToPay: {
    label: 'Бюджет к оплате нам',
    type: 'number',
  },
  statsUrl: {
    label: 'Ссылка на статистику',
    type: 'url',
  },
  product: {
    label: 'Продукт',
    type: 'select',
    enumValues: ['', 'Медиа', 'ССП', 'Иннолаба'],
  },
  category: {
    label: 'Категория',
    type: 'select',
    enumValues: [
      '',
      'Фулл-сервис',
      'Контекст',
      'Селф-сервис',
      'ССП',
      'Дин. рет ТТ',
      'Прочее',
    ],
  },
  ord: {
    label: 'Орд',
    type: 'select',
    enumValues: ['', 'ozon', 'beeline', 'VK'],
  },
  verifiers: {
    label: 'Верификаторы',
    type: 'string',
  },
  materialType: {
    label: 'Тип материалов',
    type: 'select',
    enumValues: ['', 'внешний код', 'материалы на наших серверах'],
  },
  hasContainer: {
    label: 'Контейнер стоит',
    type: 'boolean',
  },
  hasFunnelByContainer: {
    label: 'Воронка по контейнеру',
    type: 'boolean',
  },
  hasQualityAudiencePurchase: {
    label: 'Кач. ауд.закуп',
    type: 'boolean',
  },
  amParticipants: {
    label: 'Участники АМ',
    type: 'multiselect',
    enumValues: [
      'Илюшин',
      'Лаврищева',
      'Бунина',
      'Ланшакова',
      'Пастухова',
      'Фролова',
      'Паламарчук',
      'Манютина',
      'Ростовцева',
      'Аликова',
      'Яковлева',
      'Смольянинова',
      'Мельник',
      'Герасимова',
      'Никитина',
      'Дыбачевская',
      'Злобина',
    ],
  },
  cmParticipants: {
    label: 'Участники СМ',
    type: 'multiselect',
    enumValues: [
      'Бадехина',
      'Воронцова',
      'Доронин',
      'Кашталева',
      'Сараева',
      'Белая',
      'Туркевич',
      'Продажа ССП',
    ],
  },
  tmParticipants: {
    label: 'Участники ТМ',
    type: 'multiselect',
    enumValues: [
      'Аликова',
      'Олейник',
      'Яковлева',
      'Майорова',
      'Татаренко',
      'Липка',
      'Сафронова',
      'Пожарова',
      'Андросова',
      'Рубежанская',
      'Федорова',
      'Никонорова',
      'Бгатова',
      'Чегодаева',
      'Мололкина',
      'Григорьева',
      'Кузнецова',
      'Благонравова',
      'Полусмакова',
      'Селюнина',
      'Дмитриева',
      'Лихушина',
      'Разина',
      'Гражуль',
      'Акелева',
      'Бордюжа',
      'Злобина',
      'Зырянова',
      'Кривошеева',
    ],
  },
};

export type AdditionalFieldsBindings = Partial<
  Record<AdditionalFieldKey, string>
>;

export type ParsedAdditionalFields = {
  values: AdditionalFieldsModel;
  bindings: AdditionalFieldsBindings;
  mainDynamicObjectId?: string;
  legacyDynamicObjectIds: string[];
};

export function parseAdditionalFieldsFromCard(
  card: Pick<RCardGet, 'dynamicObjects'> | null,
): ParsedAdditionalFields {
  const values: AdditionalFieldsModel = {};
  const bindings: AdditionalFieldsBindings = {};
  let mainDynamicObjectId: string | undefined;
  const legacyDynamicObjectIds = new Set<string>();

  const dynamicObjects = card?.dynamicObjects ?? [];

  dynamicObjects.forEach((dynamicObject) => {
    const objDef =
      'object' in dynamicObject && dynamicObject.object != null
        ? (dynamicObject as { object: DynamicObjectDefinition }).object
        : (dynamicObject as DynamicObjectDefinition);
    const properties = objDef?.properties ?? [];
    const valuesSource =
      objDef.values ??
      (dynamicObject as { values?: Record<string, unknown> }).values;

    const additionalFieldProperties = properties.filter((property) =>
      Boolean(ADDITIONAL_FIELDS_META[property.name as AdditionalFieldKey]),
    );

    if (additionalFieldProperties.length === 0) {
      return;
    }

    if (additionalFieldProperties.length > 1) {
      if (!mainDynamicObjectId) {
        mainDynamicObjectId = dynamicObject.id;
      } else {
        legacyDynamicObjectIds.add(dynamicObject.id);
      }
    } else if (!mainDynamicObjectId) {
      legacyDynamicObjectIds.add(dynamicObject.id);
    }

    for (const property of additionalFieldProperties) {
      const key = property.name as AdditionalFieldKey;
      if (!ADDITIONAL_FIELDS_META[key]) continue;

      const rawValue = valuesSource?.[key];
      const meta = ADDITIONAL_FIELDS_META[key];
      if (rawValue === undefined || rawValue === null) continue;

      /** Первое найденное значение для ключа выигрывает — последующие дубликаты не затирают. */
      if (key in values) continue;

      bindings[key] = dynamicObject.id;

      // Осознанно ослабляем типизацию на уровне записи,
      // чтобы избежать сложных union-типов при индексировании по ключу.
      const mutableValues = values as Record<AdditionalFieldKey, unknown>;

      switch (meta.type) {
        case 'string':
        case 'url':
        case 'autocomplete': {
          const str = String(rawValue);
          mutableValues[key] = str;
          break;
        }
        case 'number': {
          const num = Number(rawValue);
          mutableValues[key] = Number.isNaN(num) ? null : num;
          break;
        }
        case 'boolean': {
          if (typeof rawValue === 'boolean') {
            mutableValues[key] = rawValue;
          } else if (typeof rawValue === 'string') {
            mutableValues[key] = rawValue === 'true';
          }
          break;
        }
        case 'date': {
          const str = String(rawValue);
          const dateOnly = str.slice(0, 10);
          mutableValues[key] = dateOnly;
          break;
        }
        case 'select': {
          const str = String(rawValue);
          mutableValues[key] = str;
          break;
        }
        case 'multiselect': {
          let arr: string[] = [];
          if (Array.isArray(rawValue)) {
            arr = rawValue.map((v) => {
              if (
                typeof v === 'number' &&
                meta.enumValues &&
                meta.enumValues[v] !== undefined
              ) {
                return String(meta.enumValues[v]);
              }
              return String(v);
            });
          } else if (rawValue != null && rawValue !== '') {
            arr = [String(rawValue)];
          }
          mutableValues[key] = arr.filter(Boolean);
          break;
        }
        default:
          break;
      }
    }
  });

  dynamicObjects.forEach((dynamicObject) => {
    if (dynamicObject.id === mainDynamicObjectId) {
      return;
    }
    legacyDynamicObjectIds.add(dynamicObject.id);
  });

  legacyDynamicObjectIds.delete(mainDynamicObjectId as string);

  // Поле «Клиентский бюджет на текущий месяц из брони» по требованию инициализируется
  // нулём, если в карточке его ещё нет (новые карточки и старые до миграции).
  if (
    values.clientBudgetCurrentMonthFromBooking === undefined
  ) {
    values.clientBudgetCurrentMonthFromBooking = 0;
  }

  return {
    values,
    bindings,
    mainDynamicObjectId,
    legacyDynamicObjectIds: Array.from(legacyDynamicObjectIds),
  };
}

/** Собирает все значения поля agencyName/clientName из dynamicObjects карточки для подстановки в knownWords словаря */
export function getWordsFromCardForField(
  card: RCardGet | null,
  key: 'agencyName' | 'clientName',
): string[] {
  const out: string[] = [];
  const list = card?.dynamicObjects ?? [];
  for (const obj of list) {
    const objDef =
      'object' in obj && obj.object != null
        ? (obj as { object: DynamicObjectDefinition }).object
        : (obj as DynamicObjectDefinition);
    const props = objDef?.properties ?? [];
    const hasKey = props.some((p) => p.name === key);
    if (!hasKey) continue;
    const valuesSource =
      objDef.values ?? (obj as { values?: Record<string, unknown> }).values;
    const raw = valuesSource?.[key];
    if (raw == null) continue;
    if (Array.isArray(raw)) {
      raw.forEach((v) => {
        const s = typeof v === 'string' ? v.trim() : String(v).trim();
        if (s) out.push(s);
      });
    } else {
      const s = typeof raw === 'string' ? raw.trim() : String(raw).trim();
      if (s) out.push(s);
    }
  }
  return Array.from(new Set(out));
}

function getEnumValuesFromDynamicProperty(
  property: DynamicProperty,
): string[] | null {
  const enumOption = property.options?.find(
    (option): option is { type: 'enum'; values: (string | number | boolean)[] } =>
      typeof option === 'object' && option.type === 'enum',
  );
  if (!enumOption) {
    return null;
  }
  return enumOption.values.map((value) => String(value));
}

function areStringArraysEqualAsSets(left: string[], right: string[]): boolean {
  const leftSet = new Set(left);
  const rightSet = new Set(right);

  if (leftSet.size !== rightSet.size) {
    return false;
  }

  return Array.from(leftSet).every((item) => rightSet.has(item));
}

/** Схема dynamic object на бэке отстаёт от ADDITIONAL_FIELDS_META (новые enum-значения и т.п.). */
export function additionalFieldsSchemaNeedsUpdate(
  existingProperties: DynamicProperty[],
): boolean {
  const propertiesByName = new Map(
    existingProperties.map((property) => [property.name, property]),
  );

  return (Object.keys(ADDITIONAL_FIELDS_META) as AdditionalFieldKey[]).some(
    (key) => {
      const meta = ADDITIONAL_FIELDS_META[key];
      const existingProperty = propertiesByName.get(key);
      if (!existingProperty) {
        return false;
      }

      const existingEnumValues = getEnumValuesFromDynamicProperty(
        existingProperty,
      );
      const metaEnumValues = meta.enumValues ?? [];

      if (metaEnumValues.length === 0) {
        // На фронте enum-ограничение снято: если на бэке enum всё ещё есть, схему нужно обновить.
        return existingEnumValues !== null;
      }

      if (!existingEnumValues) {
        return true;
      }

      return !areStringArraysEqualAsSets(
        existingEnumValues,
        metaEnumValues,
      );
    },
  );
}

export function buildDynamicObjectDefinition(
  key: AdditionalFieldKey,
  value: AdditionalFieldsModel[AdditionalFieldKey],
): DynamicObjectDefinition {
  const meta = ADDITIONAL_FIELDS_META[key];

  let storedValue: unknown = value;
  let type: 'text' | 'longText' | 'number' | 'datetime' | 'list' = 'text';

  switch (meta.type) {
    case 'multiselect': {
      type = 'list';
      let arr: string[] = [];
      if (Array.isArray(value)) {
        arr = value;
      } else if (value != null) {
        arr = [String(value)];
      }
      const enumValues = meta.enumValues ?? [];
      // В values отправляем сами значения (строки), а не индексы
      storedValue = arr.filter((v) => enumValues.includes(v));
      break;
    }
    case 'number':
      type = 'number';
      if (typeof value === 'number') {
        storedValue = value;
      } else if (value == null) {
        storedValue = null;
      } else {
        storedValue = Number(value);
      }
      break;
    case 'date':
      type = 'datetime';
      if (!value || value === '' || value === null) {
        storedValue = null;
      } else {
        const dateStr = String(value).trim();
        if (dateStr.length === 0) {
          storedValue = null;
        } else if (dateStr.length === 10) {
          storedValue = `${dateStr} 00:00:00`;
        } else if (dateStr.length > 10) {
          storedValue = dateStr;
        } else {
          storedValue = null;
        }
      }
      break;
    case 'boolean':
      type = 'text';
      storedValue = typeof value === 'boolean' ? String(value) : value ?? null;
      break;
    case 'string':
    case 'url':
    case 'autocomplete':
    case 'select':
    default:
      type = 'text';
      storedValue = value ?? null;
      break;
  }

  let options: DynamicObjectDefinition['properties'][0]['options'];
  if (type === 'list' && meta.enumValues) {
    options = [{ type: 'enum', values: meta.enumValues }];
  } else {
    const hasOptions =
      meta.required || (meta.enumValues && meta.enumValues.length > 0);
    options = hasOptions
      ? [
          ...(meta.required ? (['required'] as const) : []),
          ...(meta.enumValues
            ? [{ type: 'enum' as const, values: meta.enumValues }]
            : []),
        ]
      : undefined;
  }

  return {
    properties: [
      {
        type,
        name: key,
        label: meta.label,
        options,
      },
    ],
    values: {
      [key]: storedValue,
    },
  };
}

export function buildUnifiedDynamicObjectDefinition(
  values: AdditionalFieldsModel,
): DynamicObjectDefinition {
  const properties: DynamicObjectDefinition['properties'] = [];
  const unifiedValues: DynamicObjectValues = {};

  (Object.keys(ADDITIONAL_FIELDS_META) as AdditionalFieldKey[]).forEach(
    (key) => {
      const definition = buildDynamicObjectDefinition(key, values[key]);
      const property = definition.properties[0];
      properties.push(property);

      if (
        definition.values &&
        Object.prototype.hasOwnProperty.call(definition.values, key)
      ) {
        unifiedValues[key] = definition.values[key];
      }
    },
  );

  return {
    properties,
    values: unifiedValues,
  };
}

export type DynamicObjectExistingFields = {
  properties: DynamicProperty[];
  values?: DynamicObjectValues;
};

/** Свойства вне META (например campaignId) остаются в payload overwrite-update. */
export function preserveFieldsOutsideMeta(
  definition: DynamicObjectDefinition,
  existing: DynamicObjectExistingFields,
): DynamicObjectDefinition {
  const metaNames = new Set(
    definition.properties.map((property) => property.name),
  );
  const extraProperties = existing.properties.filter(
    (property) => !metaNames.has(property.name),
  );
  const existingValues = existing.values ?? {};
  const extraValues: DynamicObjectValues = {};
  Object.keys(existingValues).forEach((key) => {
    if (!metaNames.has(key)) {
      extraValues[key] = existingValues[key];
    }
  });

  return {
    properties: [...definition.properties, ...extraProperties],
    values: {
      ...(definition.values ?? {}),
      ...extraValues,
    },
  };
}
